import { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Paper from "@material-ui/core/Paper";
import { schema } from "./schema";
import { useFormStyles } from "./styles";
import { UseMutationResult } from "@tanstack/react-query";
import TextField from "@material-ui/core/TextField";
import WatchLater from "@material-ui/icons/WatchLater";
import ServiceField from "./components/service-field";
import { Box, Button, Grid, Typography } from "@material-ui/core";
import { handleNumberChange } from "../../../../../../../../../../../../../utils/handleNumberChange";
import { AttendanceLocalDepartment } from "../../../../../../../../../../../../../@types/attendance-local-department";
import { AttendanceLocalDepartmentQueue } from "../../../../../../../../../../../../../@types/attendance-local-department-queue";
import { AttendanceLocalDepartmentQueueServiceTime } from "../../../../../../../../../../../../../@types/attendance-local-department-queue-service-time";
import { AttendanceLocalDepartmentQueueServiceTimeCreateData } from "../../../../../../../../../../../../../services/api-tansquery/attendance-local/department/queue/service-time/create";

type FormProps = {
    queueId: AttendanceLocalDepartmentQueue["id"];
    departmentId: AttendanceLocalDepartment["id"];
    serviceTime?: AttendanceLocalDepartmentQueueServiceTime;
    mutation?: UseMutationResult<
        AttendanceLocalDepartmentQueueServiceTime,
        unknown,
        AttendanceLocalDepartmentQueueServiceTimeCreateData,
        unknown
    >
};

export default function Form({ queueId, departmentId, serviceTime, mutation }: FormProps) {
    const numberFieldProps = { min: 0, max: 99 };
    const history = useHistory();
    const { slug } = useParams<{ slug: string }>();
    const { classes } = useFormStyles();
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            fila: queueId.toString(),
            servico_id: serviceTime?.servico.toString() || "",
            tempo_presencial: "",
            tempo_online: ""
        }
    });

    useEffect(() => {
        if (serviceTime) {
            form.reset({
                fila: queueId.toString(),
                servico_id: serviceTime.servico.toString(),
                tempo_presencial: serviceTime.tempo_presencial?.toString() || "",
                tempo_online: serviceTime.tempo_online?.toString() || ""
            });
        }
    }, [form, serviceTime, queueId]);

    async function handleSubmit(data: z.infer<typeof schema>) {
        if (mutation.isLoading) {
            return;
        }

        await mutation.mutateAsync({
            fila: queueId,
            servico_id: Number(data.servico_id),
            tempo_presencial: Number(data.tempo_presencial),
            tempo_online: data.tempo_online && data.tempo_online !== "" ? Number(data.tempo_online) : undefined
        })
    }

    function preventNonNumericInput(event: React.KeyboardEvent<HTMLInputElement>) {
        if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete' && event.key !== 'Tab' && event.key !== 'Enter') {
            event.preventDefault();
        }
    }

    useEffect(() => {
        if (mutation.isSuccess) {
            history.goBack();
        }
    }, [mutation.isSuccess, history]);

    return (
        <Paper
            elevation={3}
            style={{ padding: 20 }}
            component="form"
            onSubmit={form.handleSubmit(handleSubmit)}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} component={Typography} className={classes.title}>
                    Gerenciar Configuração de Tempo (Serviço)
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="servico_id"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <ServiceField
                                field={field}
                                fieldState={fieldState}
                                unidade_slug={slug}
                                setor_unidade={departmentId}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Controller
                        name="tempo_presencial"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                required
                                fullWidth
                                type="number"
                                margin="normal"
                                variant="outlined"
                                label="Tempo de Atendimento Presencial"
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error ? fieldState.error.message : "Informe o tempo de atendimento presencial em minutos"
                                }
                                inputProps={{
                                    ...numberFieldProps,
                                    className: classes.textField,
                                }}
                                InputProps={{
                                    required: false,
                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                }}
                                onKeyPress={preventNonNumericInput}
                                onChange={(e) => {
                                    let value = handleNumberChange(
                                        numberFieldProps.min,
                                        numberFieldProps.max,
                                        e.target.value
                                    );
                                    field.onChange(value.toString());
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={6}>
                    <Controller
                        name="tempo_online"
                        control={form.control}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                fullWidth
                                type="number"
                                margin="normal"
                                variant="outlined"
                                label="Tempo de Atendimento Online"
                                error={!!fieldState.error}
                                helperText={
                                    fieldState.error ? fieldState.error.message : "Informe o tempo de atendimento online em minutos (opcional)"
                                }
                                inputProps={{
                                    ...numberFieldProps,
                                    className: classes.textField,
                                }}
                                InputProps={{
                                    required: false,
                                    endAdornment: <WatchLater htmlColor="#C4C4C4" />
                                }}
                                onKeyPress={preventNonNumericInput}
                                onChange={(e) => {
                                    let value = handleNumberChange(
                                        numberFieldProps.min,
                                        numberFieldProps.max,
                                        e.target.value
                                    );
                                    field.onChange(value === "" ? "" : value.toString());
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    onClick={() => history.goBack()}
                    disabled={mutation.isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    className={classes.saveButton}
                    disabled={mutation.isLoading}
                >
                    {mutation.isLoading ? 'Salvando...' : 'Salvar'}
                </Button>
            </Box>
        </Paper>
    );
}
